import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { AnimationClip, AnimationMixer, NumberKeyframeTrack, StringKeyframeTrack } from 'three'
import {Text} from 'troika-three-text'
import { gsap } from 'gsap'

/**
 * Panji Across SEA: Love That Transcends Time
 * Created by Stephanie V Budiman, for NUS Libraries
 * 
 * This script contains Scene 2 of "Act 2: Candra Kirana", out of 5 scenes in total
 * It begins with an animation of the characters, in the fashion of contemporary shadow puppets,
 * followed by interactivity in the form of Augmented Reality (AR) controlled via a separate web page
 * 
 * This page is optimised for viewing in 360imx
 */

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loader
 * Currently, only for the 3D models
 */

const loadingBarElement = document.querySelector('.loading-bar')

const loadingManager = new THREE.LoadingManager()

//Create animation of loading bar and fade in effect, based on overall loading progress of all models
loadingManager.onLoad = function()
{
    gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0, delay: 1 })

    loadingBarElement.classList.add('ended')
    loadingBarElement.style.transform = ''
}

loadingManager.onProgress = function ( url, itemsLoaded, itemsTotal ) {

	const progressRatio = itemsLoaded / itemsTotal
    loadingBarElement.style.transform = `scaleX(${progressRatio})`

}


/**
 * Audio
 * Original gamelan music by Singa Nglaras
 * Narration by Mak Jie Ying
 */

const listener = new THREE.AudioListener()
//since the music file is quite large, we don't add it to the loading manager, as this will slow down the start
const audioLoader = new THREE.AudioLoader()

const narration = new THREE.Audio( listener )
const bgMusic = new THREE.Audio( listener )

//Background music
audioLoader.load( '/music/monggang-demo.mp3', function( buffer) {
    bgMusic.setBuffer( buffer )
    bgMusic.setVolume( 0.2 )
    bgMusic.setLoop(true)
	//bgMusic.play()
})
//Narration voiceover
audioLoader.load( '/narrations/02_02-demo.mp3', function( buffer ) {
	narration.setBuffer( buffer )
	narration.setVolume( 1.5 )
    narration.setLoop(false)
	//narration.play()
})

/**
 * Models
 */
const loaderPaths = ['/models/ck02/king.glb', '/models/ck02/queen.glb', '/models/ck02/md.glb', '/models/ck02/liku.glb', '/models/ck02/ck.glb', '/models/ck02/ajeng.glb']

let mixers = []
let models = new Array(loaderPaths.length) //we need to create an array with the same lengths as loaderpaths but undefined contents, to be populated with the models after load

var loader = new GLTFLoader(loadingManager)

const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/')
loader.setDRACOLoader(dracoLoader)

for (let pathNum = 0; pathNum < loaderPaths.length; pathNum++)
{
    loader.load(
        loaderPaths[pathNum],
        (gltf) =>
            {
                models[pathNum] = gltf.scene
                scene.add(models[pathNum])
                models[pathNum].position.x = 0.6
                loadMixers(models[pathNum], gltf.animations) //push the model & animations info to loadMixers function so we can control the actions
            },
        undefined, (error) => {console.log(error)}
    )
}


const loadMixers = (model, animations) =>
{
    let mixer = new THREE.AnimationMixer(model)
    const numAnimations = animations.length;

    for ( let i = 0; i < numAnimations; i++ )
    {
        let action = mixer.clipAction(animations[i])
        action.setLoop(THREE.LoopOnce)
        
        const duration = 12;
        action.startAt(i*duration); //so action 0 starts at 0, 1 starts at 1*duration (eg 12), 2 starts at 2*duration (eg 24)
        action.setDuration(duration)

        action.play()
            
        if (i == numAnimations-1) //we only need to clamp the last action
        {
            action.clampWhenFinished = true 
        }
    }

    mixers.push(mixer)
}

let poisonMixer = null
//for the poison - due to its more simplistic geometry, it tends to appear faster, so a separate mixer is created to adjust the timing
loader.load(
    '/models/ck02/poison.gltf',
    (gltf) => 
    {
        let poison = gltf.scene
        scene.add(poison)
        poison.position.x = 0.6
        poisonMixer = new THREE.AnimationMixer(poison)
        let anims = gltf.animations
        for ( let i = 0; i < anims.length; i++ )
        {
            let poisoning = poisonMixer.clipAction(anims[i])
            poisoning.setLoop(THREE.LoopOnce)
        
            poisoning.startAt(i*12) //so action 0 starts at 0, 1 starts at 1*duration (eg 12), 2 starts at 2*duration (eg 24)
            poisoning.setDuration(12)

            poisoning.play()
            
            if (i == anims.length-1) //we only need to clamp the last action
            {
                poisoning.clampWhenFinished = true 
            }
        }
    },
    undefined, (error) => {console.log(error)}
)

// Add characters' labels on top of their model
const labels = ['Galuh Ajeng,\nLiku\'s daughter','Paduka Liku,\nsecond consort','Mahadewi,\nfirst consort','King of Daha','Queen of Daha','Candra Kirana,\nQueen\'s daughter']
const lTimes = [0,8,12,28,32]
const lOpacity = [0,0,1,1,0]

let labelMixers = []

for (let i in labels)
{
    let posX = -1.2+(i*0.4) // each label is 0.4 apart from each other on X axis, starting from -1.2
    const labelText = new Text()
    scene.add(labelText)
    labelText.position.set(posX,0.85,0)
    labelText.text = labels[i]
    labelText.fontSize = 0.02
    labelText.color = 'black'
    labelText.textAlign = 'center'
    labelText.sync()

    // Animate the opacity of the labels (to hide them when characters move)
    
    const lOpacityKF = new NumberKeyframeTrack('.fillOpacity', lTimes, lOpacity)
    const labelClip = new AnimationClip('labelOpacity', -1, [lOpacityKF])
    const labelMixer = new AnimationMixer(labelText)
    const labelAction = labelMixer.clipAction(labelClip)
    labelAction.play().setLoop(THREE.LoopOnce)
    labelAction.clampWhenFinished = true 

    labelMixers.push(labelMixer)
}


/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
scene.add(ambientLight)

const spotLight = new THREE.SpotLight( 0xffffff, 1)
spotLight.position.set( -2,0,4 )
spotLight.castShadow = true
scene.add( spotLight )

/**
 * Viewport
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camFactor = 1500;
const camera = new THREE.OrthographicCamera( -sizes.width / camFactor, sizes.width / camFactor, sizes.height / camFactor, - sizes.height / camFactor, 0.1, 100)
camera.position.set(0,0.5,4)
scene.add(camera)

// Controls -- enabled for dev and testings, disabled for 360imx build

const controls = new OrbitControls(camera, canvas)
controls.target.set(0,0.5,0)
controls.enableDamping = true
controls.maxZoom = 3
controls.minZoom = 0.6
controls.rotateSpeed = 0.2
controls.panSpeed = 0.2
controls.zoomSpeed = 0.6

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true
})
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor( 0x000000, 0 ); 


/**
 * Overlay
 */
 const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)
 const overlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    uniforms:
    {
        uAlpha: { value: 1 }
    },
    vertexShader: `
        void main()
        {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform float uAlpha;

        void main()
        {
            gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
        }
    `
})
 const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
 scene.add(overlay)



/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0
//const timeText = document.getElementById( 'eltime' )

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    //timeText.innerHTML = Math.round(elapsedTime)
    
    
    for (let mixer of mixers)
    {
        mixer.update(deltaTime)
    }
    

    for (let lm of labelMixers)
    {
        lm.update(deltaTime)
    }

    
        if (poisonMixer)
        {
            poisonMixer.update(deltaTime) 
        }
    

    if (elapsedTime > 60)
    {
        location.reload()
    }

    // Update controls -- only for dev, it should be disabled for 360imx build
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

//tick()


const buttonContainer = document.getElementById( 'overlay' )
const startButton = document.getElementById( 'startButton' )

startButton.addEventListener('click', function()
{
    tick()
    narration.play()
    bgMusic.play()
    buttonContainer.remove()
}
)

const returnButton = document.getElementById( 'returnButton' )

returnButton.addEventListener('click', function()
{
    location.replace("https://panji360.github.io/demos/")
}
)